import { useEffect, useState, useRef, useCallback } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import styled from "styled-components";
import { Flex, Image, Text } from "rebass/styled-components";
import { FaPlay } from "react-icons/fa";

import { analytics } from "MVVM/Analytics";
import { Button } from "MVVM/Components/Button";
import { useWatchLimit } from "MVVM/Hooks/useWatchLimit";
import { usePlaylistRefresh } from "MVVM/Hooks/usePlaylistRefresh";
import { useIsTop5Clip } from "MVVM/Hooks/useIsTop5Clip";
import {
  ActivePlaylistClipState,
  NextUpPlaylistState,
  WatchedClipsCountState,
  FinalClipInPlaylist,
  VisiblePlaylistClipsState,
} from "MVVM/State/embeddablePlaylist";
import { ActiveOverlayPopUpState, PlatformState } from "MVVM/State/iframe";
import { MatchHistorySearchBarIsOpenState } from "MVVM/State/matchHistory";
import { PlaylistClip } from "MVVM/Views/EmbeddablePlaylist/@types";
import {
  formatPlaylistClip,
  WATCH_LIMIT_EXCLUDED_PLATFORMS,
} from "MVVM/Views/EmbeddablePlaylist/Utilities";
import { CountdownCircle } from "MVVM/Views/EmbeddablePlaylist/Components/CountdownCircle";
import "./index.css";

interface NextUpPopUpProps {
  clip: any;
  showThumbnail?: boolean;
}

export const NextUpPopUp = ({
  clip,
  showThumbnail = true,
}: NextUpPopUpProps) => {
  const [countdown, setCountdown] = useState(6);
  const [nextClip, setNextClip] = useState<PlaylistClip | null>(null);
  const [countdownActive, setCountdownActive] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const visiblePlaylistClips = useRecoilValue(VisiblePlaylistClipsState);
  const setActivePlaylistClip = useSetRecoilState(ActivePlaylistClipState);
  const [activeOverlayPopUp, setActiveOverlayPopUp] = useRecoilState(
    ActiveOverlayPopUpState,
  );
  const isNextUpPlaylist = useRecoilValue(NextUpPlaylistState);
  const platform = useRecoilValue(PlatformState);
  const [isFinalClipInPLaylist, setIsFinalClipInPlaylist] =
    useRecoilState(FinalClipInPlaylist);

  const [watchedClipsCount, setWatchedClipsCount] = useRecoilState<number>(
    WatchedClipsCountState,
  );
  const isSearchBarOpen = useRecoilValue(MatchHistorySearchBarIsOpenState);

  const { handleWatchLimitReached } = useWatchLimit();
  const { refreshPlaylist } = usePlaylistRefresh();
  const isTop5Clip = useIsTop5Clip(nextClip, "FEATURED");

  useEffect(() => {
    if (
      activeOverlayPopUp === "nextUp" ||
      activeOverlayPopUp === "nextUpPlaylist"
    ) {
      setCountdown(6);
      setCountdownActive(true);
    }
  }, [clip?._id, activeOverlayPopUp]);

  useEffect(() => {
    if (!visiblePlaylistClips) return undefined;
    const index = visiblePlaylistClips.findIndex(
      (playlistClip) => playlistClip._id === clip?._id,
    );
    if (index >= 0 && index < visiblePlaylistClips.length - 1) {
      setNextClip(visiblePlaylistClips[index + 1]);
      setIsFinalClipInPlaylist(false);
    } else {
      setIsFinalClipInPlaylist(true);

      setNextClip(null);
    }
  }, [visiblePlaylistClips, clip._id, setIsFinalClipInPlaylist]);

  const updateClip = useCallback(() => {
    if (nextClip) {
      if (
        platform &&
        !WATCH_LIMIT_EXCLUDED_PLATFORMS.includes(platform) &&
        watchedClipsCount >= 2
      ) {
        setCountdownActive(false);
        return handleWatchLimitReached(clip.username || "", nextClip);
      }

      const formattedClip = formatPlaylistClip(nextClip);
      setActivePlaylistClip(formattedClip);
      setActiveOverlayPopUp(null);
      setWatchedClipsCount((prev) => prev + 1);

      if (isTop5Clip) {
        setCountdownActive(false);
        return handleWatchLimitReached(clip.username || "", nextClip);
      }
    } else {
      if (!visiblePlaylistClips) return;
      const formattedClip = formatPlaylistClip(visiblePlaylistClips[0]);
      setActivePlaylistClip(formattedClip);
      setActiveOverlayPopUp(null);
    }
  }, [
    nextClip,
    setActivePlaylistClip,
    setActiveOverlayPopUp,
    setWatchedClipsCount,
    handleWatchLimitReached,
    watchedClipsCount,
    clip.username,
    visiblePlaylistClips,
    platform,
    isTop5Clip,
  ]);

  useEffect(() => {
    if (!countdownActive) return;
    if (isSearchBarOpen) return;
    if (
      activeOverlayPopUp !== "nextUp" &&
      activeOverlayPopUp !== "nextUpPlaylist"
    )
      return;
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          updateClip();
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [
    nextClip,
    setActivePlaylistClip,
    setActiveOverlayPopUp,
    activeOverlayPopUp,
    updateClip,
    countdownActive,
    isSearchBarOpen,
  ]);

  const handleLoadMoreClips = async () => {
    analytics.track("Load More Playlist Clips Button");
    setIsLoadingMore(true);

    refreshPlaylist();
    setIsLoadingMore(false);
  };

  if (
    activeOverlayPopUp !== "nextUp" &&
    activeOverlayPopUp !== "nextUpPlaylist"
  )
    return null;

  return (
    <StyledContainer $isNextUpPlaylist={isNextUpPlaylist}>
      <StyledFixedWidth
        className="next-up-fixed-width"
        $isFinalClipInPLaylist={isFinalClipInPLaylist}
      >
        {isNextUpPlaylist && !isFinalClipInPLaylist && showThumbnail && (
          <Flex
            style={{ position: "relative", cursor: "pointer" }}
            onClick={updateClip}
          >
            <StyledThumbnailImage
              className="next-up-image"
              src={nextClip?.thumbnailStandardUrl}
            />
            <PlayIcon className="next-up-play-icon" />
          </Flex>
        )}
        <StyledTextContainer
          $isNextUpPlaylist={isNextUpPlaylist}
          $isFinalClipInPLaylist={isFinalClipInPLaylist}
        >
          {isFinalClipInPLaylist ? (
            <>
              <Flex
                flexDirection="column"
                style={{ alignItems: "center", gap: "2px" }}
              >
                <StyledNextUpText className="next-up-text">
                  Replay Playlist in:
                </StyledNextUpText>
                <CountdownCircle countdown={countdown} max={6} size={24} />
              </Flex>
              <Flex flexDirection="column">
                <StyledButton
                  onClick={handleLoadMoreClips}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? "Loading..." : "Load More Clips"}
                </StyledButton>
              </Flex>
            </>
          ) : (
            <>
              <Flex flexDirection="column">
                <StyledNextUpText className="next-up-text">
                  Next Up:
                </StyledNextUpText>
                <StyledTitleText className="next-up-title">
                  {nextClip?.title}
                </StyledTitleText>
              </Flex>
              <CountdownCircle countdown={countdown} max={6} size={24} />
            </>
          )}
        </StyledTextContainer>
      </StyledFixedWidth>
    </StyledContainer>
  );
};

const StyledContainer = styled(Flex)<{ $isNextUpPlaylist: boolean }>`
  align-items: center;
  background: rgb(0, 0, 0, 0.8);
  border-radius: ${({ $isNextUpPlaylist }) =>
    $isNextUpPlaylist ? "0" : "12px"};
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

const StyledThumbnailImage = styled(Image)`
  height: 90px;
  width: 160px;
`;

const PlayIcon = styled(FaPlay)`
  height: 35%;
  left: 56.25%;
  position: absolute;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
  width: 35%;
`;

const StyledNextUpText = styled(Text)`
  color: ${({ theme }) => theme.colors.iceAlpha60};
  font-size: 8px;
  font-weight: 500;
  line-height: 10px;
`;

const StyledTitleText = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 8px;
  font-weight: 700;
  line-height: 10px;
`;

const StyledFixedWidth = styled(Flex)<{ $isFinalClipInPLaylist: boolean }>`
  flex-direction: column;
  pointer-events: auto;
  width: ${({ $isFinalClipInPLaylist }) =>
    $isFinalClipInPLaylist ? "170px" : "160px"};
`;

const StyledTextContainer = styled(Flex)<{
  $isNextUpPlaylist: boolean;
  $isFinalClipInPLaylist: boolean;
}>`
  justify-content: space-between;
  margin-top: ${({ $isNextUpPlaylist }) => ($isNextUpPlaylist ? "10px" : "0")};

  ${({ $isFinalClipInPLaylist }) =>
    $isFinalClipInPLaylist &&
    `
    flex-direction: column;
    align-items: center;
    gap: 10px;
  `}
`;

const StyledButton = styled(Button)`
  font-size: 12px;
  height: 30px;
  line-height: 18px;
  padding: 0;
  width: 100px;
`;
